import store from '../../store';
import Errors from '../../Errors';
import ProductRating from '../ProductRating.vue';
import ProductCardMixin from '../../mixins/ProductCardMixin';
import RelatedProducts from './show/RelatedProducts.vue';
import UpSellProducts from './show/UpSellProducts.vue';

const REVIEW_IMAGE_MAX_BYTES = 1900 * 1024;
const REVIEW_IMAGE_MAX_DIMENSION = 1800;

export default {
    components: { ProductRating, RelatedProducts, UpSellProducts },

    mixins: [
        ProductCardMixin,
    ],

    props: ['product', 'reviewCount', 'avgRating', 'ratingBreakdown'],

    data() {
        return {
            price: this.product.formatted_price,
            currentReviewPage: 1,
            fetchingReviews: false,
            reviewsLoaded: false,
            addedToCart: false,
            isReviewModalOpen: false,
            reviews: {},
            addingNewReview: false,
            selectedImage: '',
            basePosition: 'left',
            selectedPosition: 'left',
            reviewForm: {},
            reviewImages: [],
            reviewImagePreviews: [],
            reviewDropzoneActive: false,
            averageRatingState: Number(this.avgRating) || 0,
            ratingBreakdownState: this.normalizeRatingBreakdown(this.ratingBreakdown),
            cartItemForm: {
                product_id: this.product.id,
                qty: 1,
                variants: {},
                options: {},
            },
            errors: new Errors(),
        };
    },

    computed: {
        totalReviews() {
            if (! this.reviews.total) {
                return this.reviewCount;
            }

            return this.reviews.total;
        },

        ratingPercent() {
            return (this.effectiveAvgRating / 5) * 100;
        },

        emptyReviews() {
            return this.reviewsLoaded && this.totalReviews === 0;
        },

        totalReviewPage() {
            return Math.max(Math.ceil(this.totalReviews / 5), 1);
        },

        effectiveAvgRating() {
            return Number(this.averageRatingState) || 0;
        },

        reviewAverageDisplay() {
            const roundedAverage = Math.round(this.effectiveAvgRating * 10) / 10;

            return Number.isInteger(roundedAverage)
                ? `${roundedAverage}`
                : roundedAverage.toFixed(1);
        },

        roundedAverageRating() {
            return Math.round(this.effectiveAvgRating);
        },

        ratingBreakdownItems() {
            return [5, 4, 3, 2, 1].map((rating) => {
                const count = Number(this.ratingBreakdownState[rating] || 0);
                const percentage = this.totalReviews > 0
                    ? (count / this.totalReviews) * 100
                    : 0;

                return { rating, count, percentage };
            });
        },

        showReviewLoader() {
            return ! this.reviewsLoaded && this.fetchingReviews;
        },
    },

    created() {
        this.fetchReviews();
    },

    mounted() {
        document.addEventListener('keydown', this.handleReviewModalKeydown);

        // $(this.$refs.upSellProducts).slick({
        //     rows: 0,
        //     dots: false,
        //     arrows: true,
        //     infinite: true,
        //     slidesToShow: 1,
        //     slidesToScroll: 1,
        //     rtl: window.Shop.rtl,
        // });
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleReviewModalKeydown);
        document.body.classList.remove('product-review-modal-open');
        this.clearReviewImagePreviews();
    },

    methods: {
        updateQuantity(qty) {
            if (qty < 1 || this.exceedsMaxStock(qty)) {
                return;
            }

            if (isNaN(qty)) {
                qty = 1;
            }

            this.cartItemForm.qty = qty;
        },

        exceedsMaxStock(qty) {
            return this.product.manage_stock && parseInt(this.product.qty) < parseInt(qty);
        },

        changeReviewPage(page) {
            this.currentReviewPage = page;

            this.fetchReviews();
        },

        openReviewImagePicker() {
            if (this.$refs.reviewImagesInput) {
                this.$refs.reviewImagesInput.click();
            }
        },

        async syncReviewImages(event) {
            await this.setReviewImages(event.target.files);

            if (this.$refs.reviewImagesInput) {
                this.$refs.reviewImagesInput.value = '';
            }
        },

        async handleReviewDrop(event) {
            this.reviewDropzoneActive = false;
            await this.setReviewImages(event.dataTransfer ? event.dataTransfer.files : []);
        },

        async setReviewImages(files) {
            const selectedFiles = Array.from(files || []).filter((file) => file.type && file.type.startsWith('image/'));

            if (! selectedFiles.length) {
                return;
            }

            const optimizedFiles = [];

            for (const file of selectedFiles) {
                optimizedFiles.push(await this.optimizeReviewImage(file));
            }

            const combinedFiles = [...this.reviewImages, ...optimizedFiles].filter(Boolean);

            if (combinedFiles.length > 3) {
                this.$notify('Mozesz dodac maksymalnie 3 zdjecia.', { type: 'warning' });
            }

            const normalizedFiles = combinedFiles.slice(0, 3);

            this.clearReviewImagePreviews();
            this.reviewImages = normalizedFiles;
            this.reviewImagePreviews = normalizedFiles.map((image, index) => ({
                id: `${image.name}-${image.size}-${image.lastModified}-${index}`,
                name: image.name,
                url: this.createReviewImageUrl(image),
            }));

            this.errors.clear('images');
            this.errors.clear('images.0');
        },

        async optimizeReviewImage(file) {
            if (! file || ! file.type || ! file.type.startsWith('image/')) {
                return null;
            }

            if (file.size <= REVIEW_IMAGE_MAX_BYTES) {
                return file;
            }

            try {
                const image = await this.loadReviewImage(file);
                let targetWidth = image.naturalWidth || image.width;
                let targetHeight = image.naturalHeight || image.height;
                const largestDimension = Math.max(targetWidth, targetHeight);

                if (largestDimension > REVIEW_IMAGE_MAX_DIMENSION) {
                    const ratio = REVIEW_IMAGE_MAX_DIMENSION / largestDimension;
                    targetWidth = Math.round(targetWidth * ratio);
                    targetHeight = Math.round(targetHeight * ratio);
                }

                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                if (! context) {
                    return file;
                }

                let quality = 0.82;
                let blob = null;

                while (quality >= 0.45) {
                    canvas.width = Math.max(1, targetWidth);
                    canvas.height = Math.max(1, targetHeight);
                    context.clearRect(0, 0, canvas.width, canvas.height);
                    context.drawImage(image, 0, 0, canvas.width, canvas.height);

                    blob = await this.canvasToBlob(canvas, 'image/jpeg', quality);

                    if (blob && blob.size <= REVIEW_IMAGE_MAX_BYTES) {
                        break;
                    }

                    quality -= 0.1;

                    if (blob && blob.size > REVIEW_IMAGE_MAX_BYTES && quality < 0.6) {
                        targetWidth = Math.round(targetWidth * 0.85);
                        targetHeight = Math.round(targetHeight * 0.85);
                    }
                }

                if (! blob) {
                    return file;
                }

                while (blob.size > REVIEW_IMAGE_MAX_BYTES && targetWidth > 640 && targetHeight > 640) {
                    targetWidth = Math.round(targetWidth * 0.85);
                    targetHeight = Math.round(targetHeight * 0.85);
                    canvas.width = Math.max(1, targetWidth);
                    canvas.height = Math.max(1, targetHeight);
                    context.clearRect(0, 0, canvas.width, canvas.height);
                    context.drawImage(image, 0, 0, canvas.width, canvas.height);

                    const resizedBlob = await this.canvasToBlob(canvas, 'image/jpeg', 0.72);

                    if (! resizedBlob) {
                        break;
                    }

                    blob = resizedBlob;
                }

                const optimizedName = file.name.replace(/\.(png|webp|jpeg|jpg)$/i, '.jpg');

                return new File([blob], optimizedName, {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                });
            } catch (error) {
                return file;
            }
        },

        loadReviewImage(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                const image = new Image();

                reader.onload = (event) => {
                    image.onload = () => resolve(image);
                    image.onerror = reject;
                    image.src = event.target.result;
                };

                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },

        canvasToBlob(canvas, type, quality) {
            return new Promise((resolve) => {
                canvas.toBlob(resolve, type, quality);
            });
        },

        createReviewImageUrl(image) {
            const urlApi = window.URL || window.webkitURL;

            return urlApi ? urlApi.createObjectURL(image) : '';
        },

        clearReviewImagePreviews() {
            const urlApi = window.URL || window.webkitURL;

            if (! urlApi) {
                this.reviewImagePreviews = [];

                return;
            }

            this.reviewImagePreviews.forEach((image) => {
                if (image && image.url) {
                    urlApi.revokeObjectURL(image.url);
                }
            });

            this.reviewImagePreviews = [];
        },

        removeReviewImage(index) {
            const preview = this.reviewImagePreviews[index];
            const urlApi = window.URL || window.webkitURL;

            if (urlApi && preview && preview.url) {
                urlApi.revokeObjectURL(preview.url);
            }

            this.reviewImages.splice(index, 1);
            this.reviewImagePreviews.splice(index, 1);

            if (this.$refs.reviewImagesInput) {
                this.$refs.reviewImagesInput.value = '';
            }

            this.errors.clear('images');
            this.errors.clear('images.0');
        },

        resetReviewImages() {
            this.clearReviewImagePreviews();
            this.reviewImages = [];
            this.reviewDropzoneActive = false;

            if (this.$refs.reviewImagesInput) {
                this.$refs.reviewImagesInput.value = '';
            }
        },

        normalizeRatingBreakdown(breakdown = {}) {
            return [1, 2, 3, 4, 5].reduce((carry, rating) => {
                carry[rating] = Number(breakdown[rating] || breakdown[String(rating)] || 0);

                return carry;
            }, {});
        },

        openReviewModal() {
            this.isReviewModalOpen = true;
            this.errors.reset();
            document.body.classList.add('product-review-modal-open');

            this.$nextTick(() => {
                const reviewFormElement = this.$refs.reviewForm;

                if (! reviewFormElement) {
                    return;
                }

                const firstField = reviewFormElement.querySelector('input[name="reviewer_name"], textarea[name="comment"]');

                if (firstField) {
                    firstField.focus();
                }
            });
        },

        closeReviewModal() {
            this.isReviewModalOpen = false;
            this.reviewDropzoneActive = false;
            document.body.classList.remove('product-review-modal-open');
        },

        handleReviewModalKeydown(event) {
            if (event.key === 'Escape' && this.isReviewModalOpen) {
                this.closeReviewModal();
            }
        },

        scrollToReviewForm() {
            this.openReviewModal();
        },

        updatePriceGallery() {
            this.$nextTick(() => {
                $.ajax({
                    method: 'POST',
                    url: route('products.price.showGallery', { id: this.product.id }),
                    data: { variants: this.cartItemForm.variants },
                }).then((price) => {
                    this.price = price;
                }).catch((xhr) => {
                    this.$notify(xhr.responseJSON.message);
                });
            });
        },

        updatePrice() {
            this.$nextTick(() => {
                $.ajax({
                    method: 'POST',
                    url: route('products.price.show', { id: this.product.id }),
                    data: { options: this.cartItemForm.options },
                }).then((price) => {
                    this.price = price;
                }).catch((xhr) => {
                    this.$notify(xhr.responseJSON.message);
                });
            });
        },

        updateSelectTypeOptionValue(optionId, e) {
            this.$set(this.cartItemForm.options, optionId, $(e.target).val());

            this.errors.clear(`options.${optionId}`);
        },

        updateCheckboxTypeOptionValue(optionId, e) {
            let values = $(e.target)
                .parents('.variant-check')
                .find('input[type="checkbox"]:checked')
                .map((_, el) => {
                    return el.value;
                });

            this.$set(this.cartItemForm.options, optionId, values.get());
        },

        variantTypeOptionValueIsActive(optionId, valueId) {
            if (! this.cartItemForm.variants.hasOwnProperty(optionId)) {
                return false;
            }

            return this.cartItemForm.variants[optionId] === valueId;
        },

        syncVariantTypeOptionValue(optionId, valueId, imagePosition, imageLink) {
            if (this.customRadioTypeOptionValueIsActive(optionId, valueId)) {
                this.$delete(this.cartItemForm.variants, optionId);
            } else {
                this.$set(this.cartItemForm.variants, optionId, valueId);
                this.errors.clear(`variants.${optionId}`);
            }

            if (optionId == "side") {
                this.basePosition = valueId;
            } else if(!isNaN(optionId)) {
                this.selectedPosition = imagePosition;
                this.selectedImage = imageLink;
            }

            this.updatePriceGallery();
        },

        customRadioTypeOptionValueIsActive(optionId, valueId) {
            if (! this.cartItemForm.options.hasOwnProperty(optionId)) {
                return false;
            }

            return this.cartItemForm.options[optionId] === valueId;
        },

        syncCustomRadioTypeOptionValue(optionId, valueId) {

            if (this.customRadioTypeOptionValueIsActive(optionId, valueId)) {
                $(document).find('.config-menu ul li a[data-tab="tab-'+optionId+'"]').removeClass('selected');
                console.log("da");
                this.$delete(this.cartItemForm.options, optionId);
            } else {
                this.$set(this.cartItemForm.options, optionId, valueId);
                $(document).find('.config-menu ul li a[data-tab="tab-'+optionId+'"]').addClass('selected');
                console.log("da2");
                this.errors.clear(`options.${optionId}`);
            }

            this.updatePrice();
        },

        customCheckboxTypeOptionValueIsActive(optionId, valueId) {
            if (! this.cartItemForm.options.hasOwnProperty(optionId)) {
                this.$set(this.cartItemForm.options, optionId, []);

                return false;
            }

            return this.cartItemForm.options[optionId].includes(valueId);
        },

        syncCustomCheckboxTypeOptionValue(optionId, valueId) {
            if (this.customCheckboxTypeOptionValueIsActive(optionId, valueId)) {
                this.cartItemForm.options[optionId].splice(
                    this.cartItemForm.options[optionId].indexOf(valueId),
                    1
                );
            } else {
                this.cartItemForm.options[optionId].push(valueId);

                this.errors.clear(`options.${optionId}`);
            }

            this.updatePrice();
        },

        fetchReviews() {
            this.fetchingReviews = true;

            $.ajax({
                method: 'GET',
                url: route('products.reviews.index', {
                    productId: this.product.id,
                    page: this.currentReviewPage,
                }),
            }).then((reviews) => {
                this.reviews = reviews;
                this.reviewsLoaded = true;
            }).catch((xhr) => {
                this.$notify(xhr.responseJSON.message);
                this.reviewsLoaded = true;
            }).always(() => {
                this.fetchingReviews = false;
            });
        },

        addNewReview() {
            this.addingNewReview = true;

            const reviewFormElement = this.$refs.reviewForm;
            const formData = reviewFormElement
                ? new FormData(reviewFormElement)
                : new FormData();

            Object.entries(this.reviewForm || {}).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.set(key, value);
                }
            });

            formData.delete('images[]');

            this.reviewImages.forEach((image) => {
                formData.append('images[]', image);
            });

            $.ajax({
                method: 'POST',
                url: route('products.reviews.store', { productId: this.product.id }),
                data: formData,
                processData: false,
                contentType: false,
            }).then((review) => {
                const previousTotal = this.totalReviews;
                const submittedRating = Number(review.rating || this.reviewForm.rating || 0);

                this.errors.reset();
                this.reviewForm = {};
                this.resetReviewImages();

                if (! this.reviews.data) {
                    this.reviews = {
                        data: [],
                        total: this.reviewCount,
                    };
                }

                this.reviewsLoaded = true;
                this.reviews.total = (this.reviews.total || 0) + 1;
                this.reviews.data.unshift(review);

                if (submittedRating >= 1 && submittedRating <= 5) {
                    this.ratingBreakdownState = {
                        ...this.ratingBreakdownState,
                        [submittedRating]: Number(this.ratingBreakdownState[submittedRating] || 0) + 1,
                    };

                    this.averageRatingState = (
                        ((this.effectiveAvgRating * previousTotal) + submittedRating)
                        / (previousTotal + 1)
                    );
                }

                $('.captcha-input').prev('img').trigger('click');
                this.closeReviewModal();
            }).catch((xhr) => {
                if (xhr.status === 422) {
                    this.errors.record(xhr.responseJSON.errors);
                } else {
                    this.$notify(xhr.responseJSON.message);
                }
            }).always(() => {
                this.addingNewReview = false;
            });
        },

        addToCart() {
            this.addingToCart = true;

            $.ajax({
                method: 'POST',
                url: route('cart.items.store', this.cartItemForm),
            }).then((cart) => {
                store.updateCart(cart);
                $('.popup-configuration').hide();
                $('.open-cart').trigger('click');
                this.addedToCart = true;
            }).catch((xhr) => {
                if (xhr.status === 422) {
                    this.errors.record(xhr.responseJSON.errors);
                } else {
                    this.$notify(xhr.responseJSON.message);
                }
                if ($('.popup-configuration').is(':visible')) {
                  $('.popup-configuration-main.b4-grid').scrollTop(0);
                }
            }).always(() => {
                this.addingToCart = false;
            });
        },

        addToCartGallery() {
            this.addingToCart = true;

            $.ajax({
                method: 'POST',
                url: route('cart.items.storeVariants', this.cartItemForm),
            }).then((cart) => {
                store.updateCart(cart);
                $('.popup-configuration').hide();
                $('.open-cart').trigger('click');
                this.addedToCart = true;
            }).catch((xhr) => {
                if (xhr.status === 422) {
                    this.errors.record(xhr.responseJSON.errors);
                } else {
                    this.$notify(xhr.responseJSON.message);
                }
            }).always(() => {
                this.addingToCart = false;
            });
        },
    },
};
